import { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { GlobalSearchDispatchContext, GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import { DATE_SEARCH_OPTION_IDS } from 'constants/search'
import config from 'constants/config'
import type { ButtonToggleItem } from 'components/Common/ButtonGroupToggle/ButtonGroupToggleItem'

interface Props {
  onAnytimeDatesSelected?: () => void
  isSpecificSearchEnabled?: boolean
}

interface DateToggle {
  id: string
  label: string
}

const useDateSearchProps = (props: Props) => {
  const { onAnytimeDatesSelected, isSpecificSearchEnabled = true } = props
  const intl = useIntl()

  const flexibleDatesToggle: DateToggle = {
    id: DATE_SEARCH_OPTION_IDS.FLEXIBLE,
    label: intl.formatMessage({
      id: 'rrsUzJiV3AKEOaVJ_Z2I',
      defaultMessage: 'Flexible dates',
    }),
  }
  const specificDatesLabel = config.ENABLE_FLEXIBLE_SEARCH
    ? intl.formatMessage({
        id: 'mcdWlmBfgduynBOo_zad',
        defaultMessage: 'Specific dates',
      })
    : intl.formatMessage({
        id: 'NhH3s251YJqD_LbU0Qjc',
        defaultMessage: 'I’ve got dates',
      })
  const specificDatesToggle: DateToggle = {
    id: DATE_SEARCH_OPTION_IDS.SPECIFIC,
    label: specificDatesLabel,
  }
  const anytimeDatesLabel = config.ENABLE_FLEXIBLE_SEARCH
    ? intl.formatMessage({
        id: 'FDcLu_Yhi2VHvPL5sI_D',
        defaultMessage: 'Anytime',
      })
    : intl.formatMessage({
        id: '03-of-tTye67AibR1PVy',
        defaultMessage: 'I’m flexible',
      })
  const anytimeDatesToggle: DateToggle = {
    id: DATE_SEARCH_OPTION_IDS.ANYTIME,
    label: anytimeDatesLabel,
  }

  const {
    checkinDate,
    checkoutDate,
    isFlexibleDateSelected,
    dateSearchOptionId,
    flexibleNights,
    flexibleMonths,
    userSelectedFlexibleMonths,
  } = useContext(GlobalSearchStateContext)

  const hasDates = !!(checkinDate && checkoutDate)
  const globalSearchDispatch = useContext(GlobalSearchDispatchContext)
  const isFlexibleSearchEnabled = config.ENABLE_FLEXIBLE_SEARCH
  const dateToggleButtons = useMemo(
    () => {
      const dataToggles: Array<DateToggle> = []
      if (isSpecificSearchEnabled) dataToggles.push(specificDatesToggle)
      if (isFlexibleSearchEnabled) dataToggles.push(flexibleDatesToggle)
      dataToggles.push(anytimeDatesToggle)
      return dataToggles
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [isFlexibleSearchEnabled, isSpecificSearchEnabled, intl],
  )

  /**
   * Handler when user select I'm flexible
   * On mobile view, will apply search
   * On desktop view, will not apply search
   * Fire UA event when I'm flexible clicked
   */

  const unsetDates = useCallback(() => {
    globalSearchDispatch({ type: GlobalSearchStateActions.UNSET_CHECKIN_DATE })
    globalSearchDispatch({ type: GlobalSearchStateActions.UNSET_CHECKOUT_DATE })
  }, [globalSearchDispatch])

  const unsetFlexibleDates = useCallback(() => {
    globalSearchDispatch({ type: GlobalSearchStateActions.UNSET_FLEXIBLE_DURATION })
    globalSearchDispatch({ type: GlobalSearchStateActions.UNSET_FLEXIBLE_MONTH_RANGE })
    globalSearchDispatch({ type: GlobalSearchStateActions.UNSET_USER_SELECTED_FLEXIBLE_MONTHS })
  }, [globalSearchDispatch])

  const selectAnytimeDates = useCallback(() => {
    unsetDates()
    unsetFlexibleDates()
    globalSearchDispatch({ type: GlobalSearchStateActions.SET_DATE_SEARCH_OPTION, optionId: DATE_SEARCH_OPTION_IDS.ANYTIME })
    globalSearchDispatch({ type: GlobalSearchStateActions.TOGGLE_ANYTIME_DATES_SELECTED, selected: true })

    onAnytimeDatesSelected?.()
  }, [globalSearchDispatch, onAnytimeDatesSelected, unsetDates, unsetFlexibleDates])

  /**
   * Reset the flexibleDateSelected when user select i've got dates or when user select dates in calendar
   */
  const selectSpecificDates = useCallback(() => {
    globalSearchDispatch({ type: GlobalSearchStateActions.TOGGLE_ANYTIME_DATES_SELECTED, selected: false })
    globalSearchDispatch({ type: GlobalSearchStateActions.SET_DATE_SEARCH_OPTION, optionId: DATE_SEARCH_OPTION_IDS.SPECIFIC })
    unsetFlexibleDates()
  }, [globalSearchDispatch, unsetFlexibleDates])

  const selectFlexibleDates = useCallback(() => {
    unsetDates()
    globalSearchDispatch({ type: GlobalSearchStateActions.TOGGLE_ANYTIME_DATES_SELECTED, selected: false })
    globalSearchDispatch({ type: GlobalSearchStateActions.SET_DATE_SEARCH_OPTION, optionId: DATE_SEARCH_OPTION_IDS.FLEXIBLE })
  }, [unsetDates, globalSearchDispatch])

  const handleDatesChanged = useCallback(
    (dates: { startDate: moment.Moment, endDate?: moment.Moment | null }) => {
      globalSearchDispatch({ type: GlobalSearchStateActions.SET_CHECKIN_DATE, date: dates.startDate })
      globalSearchDispatch({ type: GlobalSearchStateActions.SET_CHECKOUT_DATE, date: dates.endDate ?? undefined })

      if (dates.startDate || dates.endDate) {
        selectSpecificDates()
      }
    },
    [globalSearchDispatch, selectSpecificDates],
  )

  const onToggleDateSelection = useCallback((item: ButtonToggleItem, e?: React.MouseEvent) => {
    // prevent the search form submit
    e?.preventDefault()

    switch (item.id) {
      case DATE_SEARCH_OPTION_IDS.SPECIFIC:
        selectSpecificDates()
        break
      case DATE_SEARCH_OPTION_IDS.ANYTIME:
        selectAnytimeDates()
        break
      case DATE_SEARCH_OPTION_IDS.FLEXIBLE:
        selectFlexibleDates()
        break
    }
  }, [selectSpecificDates, selectAnytimeDates, selectFlexibleDates])

  const activeItemByOptionId = useMemo(
    () =>
      dateToggleButtons.find((toggle) => {
        return toggle.id === dateSearchOptionId
      }),
    [dateToggleButtons, dateSearchOptionId],
  )

  const activeItemByAnytimeDateSelected = isFlexibleDateSelected ?
    anytimeDatesToggle :
    specificDatesToggle

  const activeItem =
    isFlexibleSearchEnabled && dateSearchOptionId ?
      activeItemByOptionId :
      activeItemByAnytimeDateSelected

  const isConfirmationButtonDisabled = !!checkinDate && !checkoutDate
  return ({
    checkinDate,
    checkoutDate,
    confirmButtonDisabled: isConfirmationButtonDisabled,
    activeDateToggleButton: activeItem,
    flexibleNights,
    flexibleMonths,
    dateToggleButtons,
    onToggleDateSelection,
    handleDatesChanged,
    hasDates,
    userSelectedFlexibleMonths,
    selectFlexibleDates,
    selectAnytimeDates,
  })
}

export default useDateSearchProps
